// t518u: link a channel form to journeys. Each submission in the channel creates a journey from the mapped fields;
// the journey reports back as replies in the submission's thread. Shared by the API (authoritative) and the web settings.
import { DateTime } from 'luxon';
import type { FormField, FormFieldType, FormValues } from '../forms';
import { formatGB } from '../gbDate';

export type JourneyLinkSlot = 'destination' | 'date' | 'window_start' | 'window_end' | 'pallets_drop' | 'pallets_collect' | 'weight_kg' | 'notes';
export type JourneyLinkFields = Partial<Record<JourneyLinkSlot, string | null>>;

/** One channel's link (fleet.form_journey_links). */
export interface FormJourneyLink {
  conversation_id: string;
  enabled: boolean;
  origin_location_id: string;
  origin_name?: string | null;
  /** 'HH:MM' Europe/London: when the journey is planned to leave on the submitted date. */
  start_time: string;
  fields: JourneyLinkFields;
  updated_at?: string;
}
export interface FormJourneyLinkInfo {
  link: FormJourneyLink | null;
  /** Can this person change the link (form manager for the channel and journey planner at a building). */
  can_manage: boolean;
}
export interface FormJourneyLinkInput { enabled: boolean; origin_location_id: string; start_time?: string; fields: JourneyLinkFields }

/** What each journey detail can be read from. Destination: a building field, or a text field holding an address. */
export const JOURNEY_LINK_SLOTS: { key: JourneyLinkSlot; label: string; types: FormFieldType[]; required?: boolean }[] = [
  { key: 'destination', label: 'Destination', types: ['building', 'short_text', 'long_text'], required: true },
  { key: 'date', label: 'Date', types: ['date', 'datetime'], required: true },
  { key: 'window_start', label: 'Window from', types: ['time', 'datetime'] },
  { key: 'window_end', label: 'Window to', types: ['time', 'datetime'] },
  { key: 'pallets_drop', label: 'Pallets to drop', types: ['number'] },
  { key: 'pallets_collect', label: 'Pallets to collect', types: ['number'] },
  { key: 'weight_kg', label: 'Weight (kg)', types: ['number'] },
  { key: 'notes', label: 'Notes', types: ['short_text', 'long_text'] },
];

export const START_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Problems with a mapping against the form's fields (empty = fine). */
export function validateJourneyLinkFields(fields: FormField[], map: JourneyLinkFields): string[] {
  const errs: string[] = [];
  const used = new Map<string, string>();
  for (const slot of JOURNEY_LINK_SLOTS) {
    const id = map[slot.key];
    if (!id) { if (slot.required) errs.push(`Choose the field for ${slot.label.toLowerCase()}`); continue; }
    const f = fields.find(x => x.id === id);
    if (!f) { errs.push(`${slot.label}: that field is no longer on the form`); continue; }
    if (!slot.types.includes(f.type)) errs.push(`${slot.label}: “${f.label}” is the wrong kind of field`);
    if (f.type === 'building' && f.multiple) errs.push(`${slot.label}: “${f.label}” allows more than one building`);
    if (slot.key !== 'window_start' && slot.key !== 'window_end' && slot.key !== 'date' && used.has(id)) errs.push(`${slot.label}: “${f.label}” is already used for ${used.get(id)!.toLowerCase()}`);
    used.set(id, slot.label);
  }
  return errs;
}

/** A first guess at the mapping from field types and labels (used when the link is first switched on). */
export function suggestJourneyLinkFields(fields: FormField[]): JourneyLinkFields {
  const out: JourneyLinkFields = {};
  const by = (types: FormFieldType[], re?: RegExp) => fields.find(f => types.includes(f.type) && !(f.type === 'building' && f.multiple) && (!re || re.test(f.label)) && !Object.values(out).includes(f.id));
  out.destination = (by(['building']) ?? by(['short_text', 'long_text'], /address|destination|deliver to|where/i))?.id ?? null;
  out.date = (by(['date'], /date|day|when/i) ?? by(['date']) ?? by(['datetime']))?.id ?? null;
  out.window_start = by(['time'], /from|start|earliest|after/i)?.id ?? null;
  out.window_end = by(['time'], /to|end|until|latest|before/i)?.id ?? null;
  out.pallets_drop = by(['number'], /(drop|deliver).*pallet|pallet.*(drop|deliver)|^pallets?$/i)?.id ?? null;
  out.pallets_collect = by(['number'], /collect|pick ?up|return/i)?.id ?? null;
  out.weight_kg = by(['number'], /weight|kg/i)?.id ?? null;
  out.notes = by(['long_text', 'short_text'], /note|comment|instruction|detail/i)?.id ?? null;
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v)) as JourneyLinkFields;
}

export interface LinkedJourneyDraft {
  /** 'YYYY-MM-DD' (UK calendar date) or null when the date field is empty. */
  date: string | null;
  location_id: string | null;
  address: string | null;
  /** 'HH:MM' UK time. */
  window_start: string | null;
  window_end: string | null;
  pallets_drop: number | null;
  pallets_collect: number | null;
  weight_kg: number | null;
  notes: string | null;
}

const timeOf = (v: unknown) => typeof v === 'string' ? (/T(\d\d:\d\d)/.exec(v)?.[1] ?? (/^\d\d:\d\d$/.test(v) ? v : null)) : null;
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Journey details read from one submission's answers. */
export function journeyDraftFromAnswers(fields: FormField[], values: FormValues, map: JourneyLinkFields): LinkedJourneyDraft {
  const field = (k: JourneyLinkSlot) => fields.find(f => f.id === map[k]);
  const val = (k: JourneyLinkSlot) => (map[k] ? values[map[k]!] : undefined);
  const dest = field('destination');
  const dv = val('destination');
  const dateV = val('date');
  const notes = val('notes');
  return {
    date: typeof dateV === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateV) ? dateV.slice(0, 10) : null,
    location_id: dest?.type === 'building' && Array.isArray(dv) && dv.length ? dv[0] : null,
    address: dest && dest.type !== 'building' && typeof dv === 'string' && dv.trim() ? dv.trim().slice(0, 500) : null,
    window_start: timeOf(val('window_start')),
    window_end: timeOf(val('window_end')),
    pallets_drop: num(val('pallets_drop')),
    pallets_collect: num(val('pallets_collect')),
    weight_kg: num(val('weight_kg')),
    notes: typeof notes === 'string' && notes.trim() ? notes.trim().slice(0, 1000) : null,
  };
}

/** A UK wall-clock date and time as an ISO instant. */
export function londonIso(date: string, time: string): string {
  return DateTime.fromISO(`${date}T${time}`, { zone: 'Europe/London' }).toUTC().toISO()!;
}

const london = (iso: string) => DateTime.fromISO(iso, { zone: 'utc' }).setZone('Europe/London');
/** "Thu 29 Oct" */
export const linkDay = (iso: string) => formatGB(london(iso), 'EEE d MMM');
/** "10:40" */
export const linkTime = (iso: string) => formatGB(london(iso), 'HH:mm');

/** Plain en-GB wording for the replies a linked journey posts in its thread. */
export const journeyThreadText = {
  scheduled: (ref: string, start: string) => `Journey ${ref} scheduled for ${linkDay(start)}.`,
  assigned: (who: string, reg: string | null, changed: boolean) => `${changed ? 'Driver and vehicle changed' : 'Driver and vehicle assigned'}: ${[who, reg].filter(Boolean).join(', ')}.`,
  unassigned: () => 'Driver and vehicle removed. Waiting to be assigned.',
  rescheduled: (from: string, to: string) => linkDay(from) === linkDay(to)
    ? `Rescheduled from ${linkTime(from)} to ${linkTime(to)} on ${linkDay(to)}.` : `Rescheduled from ${linkDay(from)} to ${linkDay(to)}.`,
  setOff: (who: string, eta: string | null) => `${who} set off.${eta ? ` ETA ${linkTime(eta)}.` : ''}`,
  eta: (eta: string, was: string) => `New ETA ${linkTime(eta)} (was ${linkTime(was)}).`,
  arrived: (at: string) => `Arrived at ${linkTime(at)}.`,
  completed: (at: string, extra: string) => `Completed at ${linkTime(at)}${extra}.`,
  failed: (reason: string) => `Couldn't complete this stop: ${reason}`,
  cancelled: (ref: string) => `Journey ${ref} cancelled.`,
  changedAfterAssign: (what: string) => `The submission was edited (${what}). The journey wasn't changed because it already has a driver. Reschedule it if needed.`,
  notCreated: (reason: string) => `A journey couldn't be created from this submission: ${reason}`,
};

/** "1 pallet", "2 pallets" */
export const palletCount = (n: number) => `${n} pallet${n === 1 ? '' : 's'}`;
